import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";

export default function PaywallScreen() {
  const [paywall, setPaywall] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  const checkPaywall = useCallback(() => {
    const stored = sessionStorage.getItem("theo_paywall");
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        if (parsed.show) {
          setPaywall(parsed);
          return true;
        }
      } catch (e) {
        // ignore parse errors
      }
    }
    return false;
  }, []);

  useEffect(() => {
    checkPaywall();

    const handlePaywallEvent = (e) => {
      const newPaywall = {
        show: true,
        code: e.detail.code,
        message: e.detail.message,
        subscribe_url: e.detail.subscribe_url
      };
      setPaywall(newPaywall);
      sessionStorage.setItem("theo_paywall", JSON.stringify(newPaywall));
    };

    window.addEventListener("theo:paywall", handlePaywallEvent);
    window.addEventListener("storage", checkPaywall);

    return () => {
      window.removeEventListener("theo:paywall", handlePaywallEvent);
      window.removeEventListener("storage", checkPaywall);
    };
  }, [checkPaywall]);

  // Don't show paywall on billing pages - user needs to complete subscription
  const billingRoutes = ["/app/billing", "/billing"];
  const isOnBillingPage = billingRoutes.some(route => location.pathname.startsWith(route));
  
  useEffect(() => {
    // Clear paywall when navigating to billing pages
    if (isOnBillingPage) {
      sessionStorage.removeItem("theo_paywall");
      setPaywall(null);
      return;
    }
    checkPaywall();
  }, [location, checkPaywall, isOnBillingPage]);

  const handleSubscribe = () => {
    sessionStorage.removeItem("theo_paywall");
    let url = paywall?.subscribe_url || "/app/billing/subscribe";
    
    // If the URL is a full URL, extract the path + query for React Router
    try {
      const parsed = new URL(url, window.location.origin);
      // Only use pathname + search if it's the same origin or a known domain
      if (parsed.origin === window.location.origin || 
          parsed.hostname === "app.theocrm.com" ||
          parsed.hostname === "theocrm.com") {
        url = parsed.pathname + parsed.search;
      }
    } catch (e) {
      // url is already a relative path, use as-is
    }
    
    navigate(url);
  };

  const handleLogout = () => {
    sessionStorage.removeItem("theo_paywall");
    localStorage.removeItem("theo_token");
    window.location.href = "/login";
  };

  if (!paywall || !paywall.show) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-[9999] bg-slate-900/80 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-8 text-center">
        <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <svg className="w-8 h-8 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>
        
        <h2 className="text-2xl font-bold text-slate-900 mb-2">
          Subscribe to Continue
        </h2>
        
        <p className="text-slate-600 mb-8">
          {paywall.message}
        </p>
        
        <div className="space-y-3">
          <button
            onClick={handleSubscribe}
            className="w-full py-3 px-4 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold rounded-lg transition-colors"
          >
            Subscribe Now
          </button>
          
          <button
            onClick={handleLogout}
            className="w-full py-2 px-4 text-slate-500 hover:text-slate-700 text-sm font-medium transition-colors"
          >
            Sign out
          </button>
        </div>
      </div>
    </div>
  );
}
